import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from './Button';

export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  onConfirm,
  confirmLabel = 'Confirm',
}) {
  // Generic loading/double-submit guard for every Modal-based save — most callers
  // await their own request and only close on success, so wrapping onConfirm here
  // covers them for free instead of each page tracking its own "submitting" state.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  // Reset once the modal closes, so a stale "Saving…" doesn't linger next time it opens.
  useEffect(() => {
    if (!open) setSubmitting(false);
  }, [open]);

  if (!open) return null;

  const widthCls = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' }[size] || 'max-w-2xl';

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-neutral-900/40 dark:bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose?.();
      }}
    >
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${widthCls} overflow-hidden border border-white/20 dark:bg-ink-900 dark:border-neutral-700 max-h-[90vh] flex flex-col`}>
        <div className="px-6 py-5 border-b border-neutral-100 flex justify-between items-center bg-white dark:border-neutral-800 dark:bg-ink-900 shrink-0">
          <div>
            <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-50">{title}</h3>
            {subtitle && <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-400">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-neutral-400 hover:text-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed dark:text-neutral-500 dark:hover:text-neutral-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
        {footer !== null && (
          <div className="px-6 py-5 bg-neutral-50 flex justify-end gap-3 border-t border-neutral-100 shrink-0 dark:bg-ink-800/50 dark:border-neutral-800">
            {footer || (
              <>
                <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                {onConfirm && (
                  <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
                    {submitting ? 'Saving…' : confirmLabel}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
